import { clsx, type ClassValue } from 'clsx'
import { twMerge } from 'tailwind-merge'

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Format a number with thousand separators (e.g., 100000 -> "100,000")
 * @param num - The number to format
 * @param locale - The locale to use (default: "es-ES")
 * @returns Formatted number string
 */
export function formatNumber(num: number | null | undefined, locale: string = "es-ES"): string {
  if (num === null || num === undefined || isNaN(num)) {
    return "0"
  }
  return new Intl.NumberFormat(locale).format(num)
}

/**
 * Format a number as currency
 * @param amount - The amount to format
 * @param currency - The currency code (default: "MXN")
 * @param locale - The locale to use (default: "es-MX")
 * @returns Formatted currency string
 */
export function formatCurrency(amount: number | null | undefined, currency: string = "MXN", locale: string = "es-MX"): string {
  if (amount === null || amount === undefined || isNaN(amount)) {
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency,
    }).format(0)
  }
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency,
  }).format(amount)
}

/**
 * Format a percentage
 * @param value - The percentage value (0-100)
 * @param decimals - Number of decimal places (default: 1)
 * @returns Formatted percentage string
 */
export function formatPercentage(value: number | null | undefined, decimals: number = 1): string {
  if (value === null || value === undefined || isNaN(value)) {
    return "0%"
  }
  return `${value.toFixed(decimals)}%`
}

/**
 * Format hierarchical location (City, State, Country)
 * @param country - Country code or name
 * @param state - State code or name
 * @param city - City code or name
 * @returns Formatted location string (e.g., "Guadalajara, Jalisco, Mexico")
 */
export function formatLocationName(value: string): string {
  const known: Record<string, string> = { ciudad_de_mexico: "Ciudad de México", mexico: "México" }
  if (known[value]) return known[value]
  // Only canonical codes are transformed; already-readable names and free text stay intact.
  if (!value.includes("_") || value.includes(" ")) return value
  return value.replace(/_/g, " ").replace(/(^|\s)\p{L}/gu, letter => letter.toLocaleUpperCase("es"))
}

export function cityFieldLabel(state?: string | null): string {
  return state === "ciudad_de_mexico" ? "Alcaldía" : "Ciudad"
}

export function formatLocation(
  country: string | null | undefined,
  state: string | null | undefined,
  city: string | null | undefined
): string {
  const parts: string[] = []
  if (city) parts.push(formatLocationName(city))
  if (state) parts.push(formatLocationName(state))
  if (country) parts.push(formatLocationName(country))
  return parts.length > 0 ? parts.join(", ") : "—"
}

// ============================================
// STATUS UTILITIES
// Re-export from database.enums for backward compatibility
// ============================================

// Import from central database enums
import {
  type VacancyStatus,
  type ApplicationStatus,
  type ProcessStageStatus,
  VacancyStatusLabels,
  ApplicationStatusLabels,
  ProcessStageStatusLabels,
  isVacancyOpen as _isVacancyOpen,
} from "./database.enums"

// Re-export types (legacy aliases for backward compatibility)
export type VacancyStatusCode = VacancyStatus
export type ApplicationStatusCode = ApplicationStatus
export type ProcessStageStatusCode = ProcessStageStatus

/**
 * Normalize vacancy status from Spanish catalog values to English codes
 * Handles both English codes and Spanish values from the catalog
 */
export function normalizeVacancyStatus(status: string | null | undefined): VacancyStatus {
  if (!status) return "draft"
  
  const statusMap: Record<string, VacancyStatus> = {
    // English codes (database enum values)
    draft: "draft",
    open: "open",
    paused: "paused",
    closed: "closed",
    // Spanish values from old catalog (CSV.csv) - for migration
    "Borrador": "draft",
    "Abierta": "open",
    "Pausada": "paused",
    "Cerrada": "closed",
    "Contratada": "closed",
  }
  return statusMap[status] || "draft"
}

/**
 * Translate vacancy status from English (database) to Spanish (display)
 */
export function translateVacancyStatus(status: string | null | undefined): string {
  if (!status) return "Borrador"
  
  const statusMap: Record<string, string> = {
    draft: "Borrador",
    open: "Abierta",
    paused: "Pausada",
    closed: "Cerrada",
    // Also handle Spanish values if they somehow exist
    "Borrador": "Borrador",
    "Abierta": "Abierta",
    "Pausada": "Pausada",
    "Cerrada": "Cerrada",
    "Contratada": "Cerrada",
  }
  return statusMap[status] || "Borrador"
}

/**
 * Check if a vacancy status indicates it's open for applications
 */
export function isVacancyOpen(status: string | null | undefined): boolean {
  return normalizeVacancyStatus(status) === "open"
}

/**
 * Check if a vacancy status allows application updates
 * Applications can be updated when vacancy is "open" or "paused" (in progress)
 */
export function canUpdateApplications(status: string | null | undefined): boolean {
  const normalizedStatus = normalizeVacancyStatus(status)
  return normalizedStatus === "open" || normalizedStatus === "paused"
}

/**
 * Get the display label for a vacancy status
 */
export function getVacancyStatusLabel(status: string | null | undefined): string {
  return VacancyStatusLabels[normalizeVacancyStatus(status)]
}

/**
 * Normalize application status
 */
export function normalizeApplicationStatus(status: string | null | undefined): ApplicationStatus {
  if (!status) return "pending"
  
  const statusMap: Record<string, ApplicationStatus> = {
    // English codes (database enum values)
    pending: "pending",
    in_process: "in_process",
    accepted: "accepted",
    rejected: "rejected",
    // Spanish values - for migration
    "Pendiente": "pending",
    "En Proceso": "in_process",
    "Aceptado": "accepted",
    "Rechazado": "rejected",
  }
  return statusMap[status] || "pending"
}

/**
 * Get the display label for an application status
 */
export function getApplicationStatusLabel(status: string | null | undefined): string {
  return ApplicationStatusLabels[normalizeApplicationStatus(status)]
}

/**
 * Normalize process stage status
 */
export function normalizeProcessStageStatus(status: string | null | undefined): ProcessStageStatus {
  if (!status) return "pending"
  
  const statusMap: Record<string, ProcessStageStatus> = {
    pending: "pending",
    in_progress: "in_progress",
    completed: "completed",
    skipped: "skipped",
  }
  return statusMap[status] || "pending"
}

/**
 * Get the display label for a process stage status
 */
export function getProcessStageStatusLabel(status: string | null | undefined): string {
  return ProcessStageStatusLabels[normalizeProcessStageStatus(status)]
}

/**
 * Format a date/time in Mexico City timezone (America/Mexico_City)
 * @param date - Date string or Date object
 * @param options - Intl.DateTimeFormatOptions
 * @returns Formatted date string in Mexico City time
 */
export function formatDateMexicoCity(
  date: string | Date | null | undefined,
  options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'America/Mexico_City'
  }
): string {
  if (!date) return "—"
  
  try {
    let dateObj: Date
    
    if (typeof date === 'string') {
      // PostgreSQL timestamp without time zone returns dates like "2024-01-15 11:00:00"
      // without timezone info. Since the server stores UTC, we need to treat these as UTC.
      // Check if it's a PostgreSQL-style timestamp (has space or T, but no timezone indicator)
      const hasTimezone = date.includes('Z') || date.includes('+') || /[+-]\d{2}:?\d{2}$/.test(date)
      
      if (!hasTimezone && (date.includes('T') || /^\d{4}-\d{2}-\d{2}[\sT]\d{2}:\d{2}/.test(date))) {
        // It's a timestamp without timezone - treat as UTC
        // Convert "YYYY-MM-DD HH:mm:ss" or "YYYY-MM-DDTHH:mm:ss" to ISO with Z
        const normalizedDate = date.replace(' ', 'T').replace(/\.\d{3}$/, '') // Remove milliseconds if present
        const isoString = normalizedDate.endsWith('Z') ? normalizedDate : normalizedDate + 'Z'
        dateObj = new Date(isoString)
      } else {
        dateObj = new Date(date)
      }
    } else {
      dateObj = date
    }
    
    // Validate the date
    if (isNaN(dateObj.getTime())) {
      console.error('Invalid date:', date)
      return "—"
    }
    
    return new Intl.DateTimeFormat('es-MX', options).format(dateObj)
  } catch (error) {
    console.error('Error formatting date:', error, date)
    return "—"
  }
}

/**
 * Resolve the backend API URL based on environment variables
 * Checks NEXT_PUBLIC_API_BASE_URL, NEXT_PUBLIC_API_URL, API_BASE_URL
 * Falls back to local development URL or production URL
 * @returns The backend API base URL
 */
export function resolveBackendUrl(): string {
  const explicitUrl =
    process.env.NEXT_PUBLIC_API_BASE_URL ||
    process.env.NEXT_PUBLIC_API_URL ||
    process.env.API_BASE_URL

  if (explicitUrl) {
    return explicitUrl
  }

  if (process.env.NODE_ENV !== "production") {
    return process.env.LOCAL_WORKER_URL || "http://127.0.0.1:8787"
  }

  return "https://postulat-server-dev.deamon.workers.dev"
}

import { isSuperAdminOrOwner } from "./user-roles"

/**
 * Check if a session represents an admin user
 */
export function isAdminUser(session: { profile?: { role?: string; user_type?: string } } | null): boolean {
  return isSuperAdminOrOwner(session?.profile)
}

/**
 * Get the appropriate API endpoint prefix based on user role
 * Returns "admin" for admin users, "users" for regular users
 * @param session - Session object with profile property
 * @param resource - The resource name (e.g., "catalogs", "profile")
 * @returns The full endpoint path (e.g., "postulat/admin/catalogs" or "postulat/users/catalogs")
 */
export function getEndpointByRole(
  session: { profile?: { role?: string; user_type?: string } } | null,
  resource: string
): string {
  const prefix = isAdminUser(session) ? "postulat/admin" : "postulat/users"
  return `${prefix}/${resource}`
}

/**
 * Safely parse a session cookie value, handling URL encoding
 * @param cookieValue - The raw cookie value (may be URL-encoded or undefined)
 * @returns Parsed session object or null if parsing fails
 */
export function parseSessionCookie(cookieValue: string | undefined): any | null {
  if (!cookieValue) return null
  
  try {
    // Try to decode if it's URL-encoded (Next.js might encode it)
    let decodedValue = cookieValue
    try {
      decodedValue = decodeURIComponent(cookieValue)
    } catch {
      // If decoding fails, use the original value
      decodedValue = cookieValue
    }
    
    return JSON.parse(decodedValue)
  } catch (error) {
    console.error("Error parsing session cookie:", error)
    return null
  }
}
